import { Suspense } from "react";
import { lazyRetry } from "@/lib/lazyRetry";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ActiveSessionProvider } from "@/hooks/useActiveSession";
import { useIOSAutoZoomFix } from "@/hooks/useIOSAutoZoomFix";
import AppLayout from "@/components/layout/AppLayout";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";

const Index = lazyRetry(() => import("./pages/Index"));
const HomePage = lazyRetry(() => import("./pages/HomePage"));
const OAuthAuthorizePage = lazyRetry(() => import("./pages/OAuthAuthorizePage"));

const AuthPage = lazyRetry(() => import("./pages/Auth"));
const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const ProfileSetup = lazyRetry(() => import("./pages/ProfileSetup"));
const Admin = lazyRetry(() => import("./pages/Admin"));
const SuperAdmin = lazyRetry(() => import("./pages/SuperAdmin"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const Library = lazyRetry(() => import("./pages/Library"));
const Onboarding = lazyRetry(() => import("./pages/Onboarding"));
const Programs = lazyRetry(() => import("./pages/Programs"));
const Sessions = lazyRetry(() => import("./pages/Sessions"));
const Planning = lazyRetry(() => import("./pages/Planning"));
const SettingsPage = lazyRetry(() => import("./pages/SettingsPage"));
const WellnessPage = lazyRetry(() => import("./pages/WellnessPage"));
const Community = lazyRetry(() => import("./pages/Community"));

const Legal = lazyRetry(() => import("./pages/Legal"));
const CGU = lazyRetry(() => import("./pages/CGU"));
const Privacy = lazyRetry(() => import("./pages/Privacy"));
const ProfilePage = lazyRetry(() => import("./pages/ProfilePage"));

const IntentSelection = lazyRetry(() => import("./pages/IntentSelection"));
const GuidePage = lazyRetry(() => import("./pages/GuidePage"));
const AboutPage = lazyRetry(() => import("./pages/AboutPage"));
const CoachDashboard = lazyRetry(() => import("./pages/CoachDashboard"));
const CoachEntityDetail = lazyRetry(() => import("./pages/CoachEntityDetail"));
const CoachCreateProgram = lazyRetry(() => import("./pages/CoachCreateProgram"));
const CoachTraining = lazyRetry(() => import("./pages/CoachTraining"));
const CoachGroups = lazyRetry(() => import("./pages/CoachGroups"));
const CoachGroupDetail = lazyRetry(() => import("./pages/CoachGroupDetail"));
const CoachGroupSessions = lazyRetry(() => import("./pages/CoachGroupSessions"));
const CoachAcceptAthleteInvite = lazyRetry(() => import("./pages/CoachAcceptAthleteInvite"));

const HealthTraining = lazyRetry(() => import("./pages/HealthTraining"));
const HealthDashboard = lazyRetry(() => import("./pages/HealthDashboard"));
const HealthEntityDetail = lazyRetry(() => import("./pages/HealthEntityDetail"));
const HealthAcceptInvite = lazyRetry(() => import("./pages/HealthAcceptInvite"));
const HealthGroups = lazyRetry(() => import("./pages/HealthGroups"));
const HealthGroupDetail = lazyRetry(() => import("./pages/HealthGroupDetail"));
const Challenges = lazyRetry(() => import("./pages/Challenges"));
const CoachPlanning = lazyRetry(() => import("./pages/CoachPlanning"));
const Billing = lazyRetry(() => import("./pages/Billing"));
const AchievementsPage = lazyRetry(() => import("./pages/AchievementsPage"));
const MeritShopPage = lazyRetry(() => import("./pages/MeritShopPage"));
const QuestionnairesPage = lazyRetry(() => import("./pages/QuestionnairesPage"));
const QuickQuestionnairePage = lazyRetry(() => import("./pages/QuickQuestionnairePage"));
const ProPublicProfilePage = lazyRetry(() => import("./pages/ProPublicProfilePage"));

// V3 — Scaffolded future modules
const OptionalExtrasPage = lazyRetry(() => import("./pages/OptionalExtrasPage"));
const ReferralPage = lazyRetry(() => import("./pages/ReferralPage"));
const PromoCodesPage = lazyRetry(() => import("./pages/PromoCodesPage"));
const BuildGuidePage = lazyRetry(() => import("./pages/BuildGuidePage"));
const IPProtectionPage = lazyRetry(() => import("./pages/IPProtectionPage"));
const MentalTrainingPage = lazyRetry(() => import("./pages/MentalTrainingPage"));
const BrainMethodsPage = lazyRetry(() => import("./pages/BrainMethodsPage"));
const KnowledgeGapsPage = lazyRetry(() => import("./pages/KnowledgeGapsPage"));
const AdminHealthBrainPage = lazyRetry(() => import("./pages/AdminHealthBrainPage"));
const NutritionKnowledgePage = lazyRetry(() => import("./pages/NutritionKnowledgePage"));
const PrivacyAlertsPage = lazyRetry(() => import("./pages/PrivacyAlertsPage"));
const DataSecurityPage = lazyRetry(() => import("./pages/DataSecurityPage"));
const PromptSystemPage = lazyRetry(() => import("./pages/PromptSystemPage"));
const BrainArchitecturePage = lazyRetry(() => import("./pages/BrainArchitecturePage"));
const ManualModePage = lazyRetry(() => import("./pages/ManualModePage"));
const FuturePathsPage = lazyRetry(() => import("./pages/FuturePathsPage"));
const ParentCoachingPage = lazyRetry(() => import("./pages/ParentCoachingPage"));
const SocialLinksPage = lazyRetry(() => import("./pages/SocialLinksPage"));
const MeritEconomyPage = lazyRetry(() => import("./pages/MeritEconomyPage"));
const ProfileUpdatePromptsPage = lazyRetry(() => import("./pages/ProfileUpdatePromptsPage"));
const BrainPreferencesPage = lazyRetry(() => import("./pages/BrainPreferencesPage"));
const NutritionPreferencesPage = lazyRetry(() => import("./pages/NutritionPreferencesPage"));
const BrainPopupsPage = lazyRetry(() => import("./pages/BrainPopupsPage"));
const FeatureUpdateAlertsPage = lazyRetry(() => import("./pages/FeatureUpdateAlertsPage"));
const XPSystemPage = lazyRetry(() => import("./pages/XPSystemPage"));

const AthleteProfilePage = lazyRetry(() => import("./pages/AthleteProfile"));
const ListeningPage = lazyRetry(() => import("./pages/Listening"));

// New stub routes
const NutritionSupplementsPage = lazyRetry(() => import("./pages/NutritionSupplementsPage"));
const SocialFriendsPage = lazyRetry(() => import("./pages/SocialFriendsPage"));
const ContactsPage = lazyRetry(() => import("./pages/ContactsPage"));
const AthleteGroupsPage = lazyRetry(() => import("./pages/AthleteGroupsPage"));
const ScienceCardsPage = lazyRetry(() => import("./pages/ScienceCardsPage"));
const PlanningCentralPage = lazyRetry(() => import("./pages/PlanningCentralPage"));
const WellnessPreventionPage = lazyRetry(() => import("./pages/WellnessPreventionPage"));
const PreventionPage = lazyRetry(() => import("./pages/PreventionPage"));
const ChallengesPage = lazyRetry(() => import("./pages/ChallengesPage"));
const LibraryTopicsPage = lazyRetry(() => import("./pages/LibraryTopicsPage"));
const CalendarSyncPage = lazyRetry(() => import("./pages/CalendarSyncPage"));
const CalendarGooglePage = lazyRetry(() => import("./pages/CalendarGooglePage"));
const AvatarPage = lazyRetry(() => import("./pages/AvatarPage"));
const ProgressionChartsPage = lazyRetry(() => import("./pages/ProgressionChartsPage"));
const MeritWaitlistPage = lazyRetry(() => import("./pages/MeritWaitlistPage"));
const SubscriptionPage = lazyRetry(() => import("./pages/SubscriptionPage"));
const ProMarketplacePage = lazyRetry(() => import("./pages/ProMarketplacePage"));
const BrainRouterPage = lazyRetry(() => import("./pages/BrainRouterPage"));
const BrainSessionsPage = lazyRetry(() => import("./pages/BrainSessionsPage"));

// V4 — Expansion paths
const VisualModelsPage = lazyRetry(() => import("./pages/VisualModelsPage"));
const AthleteComparisonPage = lazyRetry(() => import("./pages/AthleteComparisonPage"));
const ExperienceModePage = lazyRetry(() => import("./pages/ExperienceModePage"));
const InterfaceModesPage = lazyRetry(() => import("./pages/InterfaceModesPage"));
const FavoritesSystemPage = lazyRetry(() => import("./pages/FavoritesSystemPage"));
const MeritPriceRulesPage = lazyRetry(() => import("./pages/MeritPriceRulesPage"));
const InternalCommercePage = lazyRetry(() => import("./pages/InternalCommercePage"));
const MeritMarketplacePage = lazyRetry(() => import("./pages/MeritMarketplacePage"));
const BetaAccessPage = lazyRetry(() => import("./pages/BetaAccessPage"));
const ModuleTutorialsPage = lazyRetry(() => import("./pages/ModuleTutorialsPage"));
const AdminReviewHubPage = lazyRetry(() => import("./pages/AdminReviewHubPage"));
const ModulesPage = lazyRetry(() => import("./pages/ModulesPage"));
const FeaturesHub = lazyRetry(() => import("./pages/FeaturesHub"));
/**
 * SYNC RULES — Global QueryClient Configuration
 * ─────────────────────────────────────────────────
 * 1. Every Supabase mutation MUST use useMutation with onSuccess → invalidateQueries
 * 2. Every data fetch MUST use useQuery — no raw useState+useEffect fetch patterns
 * 3. Profile-impacting mutations MUST call emitProfileChange() after invalidation
 * 4. staleTime 30s = compromise between data freshness and server load at 100k+ users
 * 5. refetchOnWindowFocus ensures data refresh when user returns to tab
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">Chargement...</div>;
  if (!user) return <Navigate to="/auth" replace />;
  return (
    <AppLayout>
      <RouteErrorBoundary>
        {children}
      </RouteErrorBoundary>
    </AppLayout>
  );
};

const LazyFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">Chargement...</div>
);

const App = () => {
  useIOSAutoZoomFix();

  return (
    <ThemeProvider attribute="class" defaultTheme="dark" storageKey="perform-theme">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <ActiveSessionProvider>
            <ErrorBoundary>
            <Suspense fallback={<LazyFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/legal" element={<Legal />} />
                <Route path="/cgu" element={<CGU />} />
                <Route path="/confidentialite" element={<Privacy />} />
                <Route path="/about" element={<Navigate to="/a-propos" replace />} />
                <Route path="/a-propos" element={<AboutPage />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
                <Route
                  path="/home"
                  element={
                    <ProtectedRoute>
                      <HomePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/programs"
                  element={
                    <ProtectedRoute>
                      <Programs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sessions"
                  element={
                    <ProtectedRoute>
                      <Sessions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/planning"
                  element={
                    <ProtectedRoute>
                      <Planning />
                    </ProtectedRoute>
                  }
                />
                {/* Nutrition → features (pas de section autonome encore) */}
                <Route path="/nutrition" element={<Navigate to="/features" replace />} />
                {/* Prévention → page autonome */}
                <Route path="/prevention" element={<ProtectedRoute><PreventionPage /></ProtectedRoute>} />
                <Route
                  path="/wellness"
                  element={
                    <ProtectedRoute>
                      <WellnessPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/community"
                  element={
                    <ProtectedRoute>
                      <Community />
                    </ProtectedRoute>
                  }
                />
                {/* Ghost page — gated: redirect to dashboard until properly integrated */}
                <Route path="/avatar" element={<Navigate to="/features" replace />} />
                <Route
                  path="/library"
                  element={
                    <ProtectedRoute>
                      <Library />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProfileSetup />
                    </ProtectedRoute>
                  }
                />
                <Route path="/reasoning" element={<Navigate to="/dashboard" replace />} />
                <Route
                  path="/intent"
                  element={
                    <ProtectedRoute>
                      <IntentSelection />
                    </ProtectedRoute>
                  }
                />
                <Route path="/ecoute" element={<Navigate to="/athlete-type?tab=ecoute" replace />} />
                <Route
                  path="/onboarding"
                  element={
                    <ProtectedRoute>
                      <Onboarding />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/athlete-profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/my-profile" element={<Navigate to="/athlete-profile" replace />} />
                <Route
                  path="/athlete-type"
                  element={
                    <ProtectedRoute>
                      <AthleteProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/listening"
                  element={
                    <ProtectedRoute>
                      <ListeningPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/questionnaires"
                  element={
                    <ProtectedRoute>
                      <QuestionnairesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/questionnaires/:id"
                  element={
                    <ProtectedRoute>
                      <QuickQuestionnairePage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/athlete-hub" element={<Navigate to="/dashboard" replace />} />
                <Route
                  path="/guide"
                  element={
                    <ProtectedRoute>
                      <GuidePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <Admin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/super-admin"
                  element={
                    <ProtectedRoute>
                      <SuperAdmin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/coach/training"
                  element={
                    <ProtectedRoute>
                      <CoachTraining />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/coach"
                  element={
                    <ProtectedRoute>
                      <CoachDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/coach/entity/:entityId"
                  element={
                    <ProtectedRoute>
                      <CoachEntityDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/coach/entity/:entityId/new-program"
                  element={
                    <ProtectedRoute>
                      <CoachCreateProgram />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/coach/accept-athlete/:token"
                  element={
                    <ProtectedRoute>
                      <CoachAcceptAthleteInvite />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/coach/groups"
                  element={
                    <ProtectedRoute>
                      <CoachGroups />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/coach/groups/:groupId"
                  element={
                    <ProtectedRoute>
                      <CoachGroupDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/coach/group-sessions"
                  element={
                    <ProtectedRoute>
                      <CoachGroupSessions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/health/training"
                  element={
                    <ProtectedRoute>
                      <HealthTraining />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/health"
                  element={
                    <ProtectedRoute>
                      <HealthDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/health/entity/:entityId"
                  element={
                    <ProtectedRoute>
                      <HealthEntityDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/health/accept/:token"
                  element={
                    <ProtectedRoute>
                      <HealthAcceptInvite />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/health/groups"
                  element={
                    <ProtectedRoute>
                      <HealthGroups />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/health/groups/:groupId"
                  element={
                    <ProtectedRoute>
                      <HealthGroupDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/community/challenges"
                  element={
                    <ProtectedRoute>
                      <Challenges />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/coach/planning"
                  element={
                    <ProtectedRoute>
                      <CoachPlanning />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/billing"
                  element={
                    <ProtectedRoute>
                      <Billing />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/achievements"
                  element={
                    <ProtectedRoute>
                      <AchievementsPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/shop" element={<Navigate to="/merit-shop" replace />} />
                {/* Pro public profile */}
                <Route path="/pro/:userId" element={<ProtectedRoute><ProPublicProfilePage /></ProtectedRoute>} />
                {/* V3 — Scaffolded future modules (all flag-gated, redirect to /dashboard if disabled) */}
                <Route path="/extras" element={<Navigate to="/features" replace />} />
                <Route path="/referral" element={<Navigate to="/features" replace />} />
                <Route path="/promo-codes" element={<Navigate to="/features" replace />} />
                <Route path="/build-guide" element={<BuildGuidePage />} />
                <Route path="/ip-protection" element={<Navigate to="/features" replace />} />
                <Route path="/mental-training" element={<Navigate to="/features" replace />} />
                <Route path="/brain-methods" element={<Navigate to="/features" replace />} />
                <Route path="/knowledge-gaps" element={<Navigate to="/features" replace />} />
                <Route path="/admin/health-brain" element={<ProtectedRoute><AdminHealthBrainPage /></ProtectedRoute>} />
                <Route path="/nutrition-knowledge" element={<Navigate to="/features" replace />} />
                <Route path="/privacy-alerts" element={<Navigate to="/features" replace />} />
                <Route path="/data-security" element={<Navigate to="/features" replace />} />
                <Route path="/prompt-system" element={<Navigate to="/features" replace />} />
                <Route path="/brain-architecture" element={<Navigate to="/features" replace />} />
                <Route path="/manual-mode" element={<Navigate to="/features" replace />} />
                <Route path="/future-paths" element={<Navigate to="/features" replace />} />
                <Route path="/parent-coaching" element={<Navigate to="/features" replace />} />
                <Route path="/social-links" element={<Navigate to="/features" replace />} />
                <Route path="/economy" element={<Navigate to="/features" replace />} />
                <Route path="/profile-updates" element={<Navigate to="/features" replace />} />
                <Route path="/brain-preferences" element={<Navigate to="/features" replace />} />
                <Route path="/nutrition-preferences" element={<Navigate to="/features" replace />} />
                <Route path="/brain-popups" element={<Navigate to="/features" replace />} />
                <Route path="/feature-update-alerts" element={<Navigate to="/features" replace />} />
                <Route path="/xp-system" element={<Navigate to="/features" replace />} />
                {/* V4 — Expansion paths */}
                <Route path="/visual-models" element={<Navigate to="/features" replace />} />
                <Route path="/athlete-comparison" element={<Navigate to="/features" replace />} />
                <Route path="/experience-mode" element={<Navigate to="/features" replace />} />
                <Route path="/interface-modes" element={<Navigate to="/features" replace />} />
                <Route path="/favorites-system" element={<Navigate to="/features" replace />} />
                <Route path="/merit-pricing-rules" element={<Navigate to="/features" replace />} />
                <Route path="/internal-commerce" element={<Navigate to="/features" replace />} />
                <Route path="/marketplace" element={<Navigate to="/features" replace />} />
                <Route path="/beta-access" element={<Navigate to="/features" replace />} />
                <Route path="/module-tutorials" element={<Navigate to="/features" replace />} />
                {/* Admin tools */}
                <Route path="/admin/review-hub" element={<ProtectedRoute><AdminReviewHubPage /></ProtectedRoute>} />
                {/* New stub routes — redirect to /features */}
                <Route path="/nutrition/supplements" element={<Navigate to="/features" replace />} />
                <Route path="/social/friends" element={<Navigate to="/features" replace />} />
                <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
                <Route path="/groups" element={<ProtectedRoute><AthleteGroupsPage /></ProtectedRoute>} />
                <Route path="/science-cards" element={<Navigate to="/features" replace />} />
                <Route path="/planning-central" element={<Navigate to="/features" replace />} />

                <Route path="/wellness/prevention" element={<Navigate to="/prevention" replace />} />
                <Route path="/challenges" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
                <Route path="/library/topics" element={<Navigate to="/features" replace />} />
                <Route path="/calendar/sync" element={<ProtectedRoute><CalendarSyncPage /></ProtectedRoute>} />
                <Route path="/calendar/google" element={<Navigate to="/calendar/sync" replace />} />
                <Route path="/progression/xp" element={<Navigate to="/features" replace />} />
                <Route path="/progression/charts" element={<ProtectedRoute><ProgressionChartsPage /></ProtectedRoute>} />
                <Route path="/merit-shop" element={<ProtectedRoute><MeritShopPage /></ProtectedRoute>} />
                <Route path="/merit-waitlist" element={<Navigate to="/features" replace />} />
                <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
                <Route path="/pro/marketplace" element={<Navigate to="/features" replace />} />
                <Route path="/brain/router" element={<Navigate to="/features" replace />} />
                <Route path="/brain/sessions" element={<Navigate to="/features" replace />} />
                <Route path="/modules" element={<ProtectedRoute><ModulesPage /></ProtectedRoute>} />
                <Route path="/features" element={<ProtectedRoute><FeaturesHub /></ProtectedRoute>} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </ErrorBoundary>
            </ActiveSessionProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
    </ThemeProvider>
  );
};

export default App;
